import { Component, Event, EventEmitter, Fragment, h, Method, Prop, State } from '@stencil/core';
import { VALIDATION_ATTRIBUTES } from '../../vs-form-builder.model';
import { checkValidations, getValidStateAccordingToRequiredStatus } from '../../helpers/validator.helper';
import { CheckboxAttr, VsCheckboxData } from './vs-checkbox.model';
import { decodeHTMLEntities } from '@src/components/vs-form-builder/helpers/rules.helper';


@Component({
  tag: 'vs-checkbox',
  shadow: false,
})

export class VsCheckbox {

  /**
   * @shortDescription Identifier of the input field.
   * @controlName inputId */
  @Prop() inputId: string;

  /**
   * @shortDescription Index of input field in a form.
   * @controlName index */
  @Prop() index: number;

  /**
   * @shortDescription Define the label for input field.
   * @controlName label */
  @Prop() label: string;

  /**
   * @shortDescription If declared, it is the input's default value.
   * @controlName defaultValue */
  @Prop() defaultValue: string[] = [];

  /**
   * @shortDescription Input's type
   * @controlName type */
  @Prop() type: string = 'text';

  /**
   * @shortDescription Input's placeholder
   * @controlName placeholder */
  @Prop() placeholder: string;

  /**
   * @shortDescription Validators rules to check against for the input.
   * @controlName validators */
  @Prop() validators: any[] = [];

  /**
   * @shortDescription Maxlength accepted by the input field.
   * @controlName maxLength */
  @Prop() maxLength: number;

  /**
   @shortDescription required text label
   @controlName requiredText
   */
  @Prop() requiredText: string;

  /**
   * @shortDescription Enable inline view of radio button
   * @controlName isInline */
  @Prop() isInline: boolean;

  /* START GENAI@GHCOPILOT */
  /**
   * @shortDescription HTML content to be inserted before the form field
   * @controlName aboveInputContent
   */
  @Prop() aboveInputContent: string;

  /**
   * @shortDescription HTML content to be inserted after the form field
   * @controlName belowInputContent
   */
  @Prop() belowInputContent: string;

  /**
   * @shortDescription HTML content to be inserted after the form field
   * @controlName betweenInputContent
   */
  @Prop() betweenInputContent: string;
  /* END GENAI@GHCOPILOT */

  @State() showError: boolean = false;
  @State() value: string[] = [];
  @State() selectedErrorMessage: string = '';
  @State() valid: boolean = false;
  @State() inputAllyAlert: string = 'input_alert_';
  @State() isRequired: boolean;
  @State() isAllRequired: boolean;

  /**
   * @shortDescription The data to render for the radio group
   * @controlName data */
  @Prop() data!: VsCheckboxData[];

  /**
   * @shortDescription Tooltip text for the input field.
   * @controlName tooltip */
  @Prop() tooltip: string;

  @State() innerDataState!: VsCheckboxData[];

  /**
   * @shortDescription Emit Input blur event to parent component on input blur
   * @controlName blurEvent */
  @Event() blurEvent: EventEmitter<void>;

  private inputEl?;

  /**
   * @shortDescription Emit input value on change
   * @controlName computedInputValue */
  @Event({
    eventName: 'computedInputValue',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) computedInputValue: EventEmitter;

  /**
   * @shortDescription Reset value when reset button is clicked
   * @controlName resetValue */
  @Method()
  async resetValue(isShowError = false) {
    let fieldID = this.inputId;
    let inputs = (document.getElementById(fieldID))?.querySelectorAll('input:checked');
    for (let i = 0; i < inputs.length; i++) {
      (inputs[i] as HTMLInputElement).checked = false;
    }
    this.initializeCheckboxState();
    this.showError = isShowError;
  }

  /**
   * @shortDescription set input focus
   * @controlName setFocus */
  @Method()
  async setFocus() {
    this.performValidation(true);
    this.inputEl.focus();
  }

  componentWillLoad() {
    this.initializeCheckboxState();
  }

  // START GENAI@GHCOPILOT
  initializeCheckboxState() {
    this.initializeDataState();
    this.checkRequiredValidators();
    this.performValidation();
    this.emitComputedInputValue([]);
  }
  // END GENAI@GHCOPILOT

  initializeDataState() {
    this.innerDataState = this.data;
  }

  checkRequiredValidators() {
    this.isRequired = this.hasValidator(VALIDATION_ATTRIBUTES.ALL_REQUIRED, VALIDATION_ATTRIBUTES.ONE_REQUIRED);
    this.isAllRequired = this.hasValidator(VALIDATION_ATTRIBUTES.ALL_REQUIRED);
  }

  hasValidator(...validatorKeys) {
    return this.validators.findIndex(validator => validatorKeys.includes(validator.key)) > -1;
  }

  performValidation(showError: boolean = false) {
    this.validate(this.getValidateValue(), showError);
  }

  emitComputedInputValue(value) {
    this.computedInputValue.emit({
      index: this.index,
      key: this.inputId,
      value: value,
      isValid: this.valid,
      error: this.selectedErrorMessage,
    } as CheckboxAttr);
  }


  private onBlurInput = () => {
    this.showError = true;
    this.blurEvent.emit();
  };


  private validate = (value: string, showError: boolean) => {
    const validationAttr = checkValidations(this.validators, value);
    this.selectedErrorMessage = validationAttr.errorMessage;
    this.valid = getValidStateAccordingToRequiredStatus((this.isRequired || this.isAllRequired), value, validationAttr);
    if (showError) {
      this.showError = !this.valid;
    }
  };

  private onInputChange = (event: any, elValue: string) => {
    const { target } = event;
    this.innerDataState = this.innerDataState.map(checkbox => {
      if (checkbox.value == elValue) {
        return {
          ...checkbox,
          checked: !checkbox.checked,
        };
      }
      return checkbox;
    });
    this.updateInput(target?.value);
  };

  private getValidateValue() {
    return this.isAllRequired ? this.numberUnchecked().toString() : this.numberChecked().toString();
  }

  private updateInput = (value: string) => {
    if (typeof value === 'object') {
      this.value = this.defaultValue;
    } else {
      let found = this.value.indexOf(value);
      found === -1 ? this.value.push(value) : this.value.splice(found, 1);
    }
    this.performValidation(true);
    this.emitComputedInputValue(this.value);
  };

  private numberChecked = () => {
    let inputs = this.inputEl;
    if (inputs) {
      return inputs.querySelectorAll('input:checked').length;
    }
    return -1;
  };

  private numberUnchecked = () => {
    return (this.data.length - this.numberChecked());
  };

  private generateAriaLabel = () => {
    return (this.showError && !this.valid && this.selectedErrorMessage) ? this.inputAllyAlert + this.inputId : undefined;
  };

  /* START GENAI@GHCOPILOT */
  render() {
    return (
      <Fragment>
        {this.aboveInputContent && <div class='vs-text vs-mb-0' innerHTML={this.aboveInputContent} />}
        <fieldset id={this.inputId} class='vs-pb-0 vs-mb-0'
                  ref={el => this.inputEl = el as HTMLFieldSetElement}>
          <div class='vs-legend-container'>
            {this.label && (<legend aria-describedby={this.generateAriaLabel()}
                                    class='vs-text vs-mb-1'>
              <span innerHTML={this.label}/>
              {(this.isRequired || this.isAllRequired) ?
                <strong class='required'><i> {this.requiredText} </i></strong> : ''}

            </legend>)}
            {this.tooltip?.length > 0 && <vs-tooltip
              tooltip-id={`tooltip${this.inputId}`}
              ally-open-label={`Open tooltip for ${this.label}`}
              tooltip-header={this.label}
              tooltip-body={this.tooltip}/>
            }
          </div>
            {this.betweenInputContent && <div class='vs-text vs-mb-0' innerHTML={this.betweenInputContent}/>}
            <div class={this.isInline ? 'vs-d-flex vs-form-group vs-mb-1 vs-mx-0' : 'vs-form-group vs-mb-1'}>
              {this.innerDataState && (
                this.innerDataState.map((el: VsCheckboxData, idx: number) => {
                  return (
                    <div class={`vs-checkbox-section vs-pb-1 vs-flex-grow-1`}>
                      <input type='checkbox'
                             value={idx}
                             name={`${this.inputId}-${idx}`}
                             id={`${this.inputId}-${idx}`}
                             aria-invalid={this.showError && !this.valid}
                             aria-describedby={this.generateAriaLabel()}
                             aria-checked={el.checked ? el.checked : false}
                             class={'vs-checkbox ' + (this.showError && !this.valid ? 'has-error' : '')}
                             onInput={(event: InputEvent) => this.onInputChange(event, el.value)}
                             disabled={el.disabled}
                             onBlur={this.onBlurInput}/>
                      <label htmlFor={`${this.inputId}-${idx}`}
                             class='vs-text'>
                        <span innerHTML={el.value}/>
                        {(!this.label && (this.isRequired || this.isAllRequired))
                          ? <strong class='required'><i> {this.requiredText} </i></strong> : ''}
                      </label>
                    </div>);
                })
              )
              }
            </div>
            {this.showError && !this.valid && this.selectedErrorMessage?.trim() && (
              <p role='alert' aria-live='polite' class='vs-meta vs-text vs-alert vs-inline-alert vs-pb-2'
                 id={this.inputAllyAlert + this.inputId}>
                <span aria-hidden='true' class='visa-icon-alert'/>
                <span innerHTML={decodeHTMLEntities(this.selectedErrorMessage)}/>
              </p>
            )}
        </fieldset>
        {this.belowInputContent && <div class='vs-text vs-mb-0' innerHTML={this.belowInputContent} />}
      </Fragment>
    );
  };

  /* END GENAI@GHCOPILOT */
}
